import { Link } from "react-router-dom";
import useCurrentState from "@/routes/useCurrentState";
import { isTrackActive, listStates, statePath } from "@/lib/stateRegistry";
import { PUBLIC_LAUNCH_STATE_CODE, SHOW_OTHER_STATES } from "@/lib/displayFlags";
import BrandMark from "@/components/BrandMark";
import { LEGAL_FACTS_READY } from "@/lib/legalConfig";
import { NEWSLETTER_PATH, NEWSLETTER_COPY, newsletterSignupEnabled } from "@/lib/newsletterConfig";
import { PUCT_REGISTRATION_VERIFIED, REGISTERED_BROKER_FOOTER_IDENTITY, REGISTERED_BROKER_FOOTER_NUMBER } from "@/lib/brokerRegistration";

export default function Footer() {
  const state = useCurrentState();
  return (
    <footer className="bg-[#0A2540] mt-24" data-testid="site-footer">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-3 gap-10">
        <div>
          <div className="mb-4">
            <BrandMark className="text-xl" variant="dark" />
          </div>
          <p className="text-sm text-slate-300 max-w-[32ch]">Know your account, before you shop.</p>
          {/* Non-affiliation identity block: required and visible now (SEO_Copy_Rulings.md 2.5). The "must NOT render
              before registration" rule applies only to the registered-broker line below. */}
          <p className="text-xs text-slate-400 mt-4 max-w-[44ch]" data-testid="footer-non-affiliation">
            RateCurrent is not a retail electric provider or utility. RateCurrent is not affiliated with or endorsed by the Public Utility Commission of Texas, ERCOT, or any Texas utility.
          </p>
          {/* Registered-broker line (Owner order 2026-10-15): every PublicShell page, one line, identity sentence + number, no badge/link.
              Rendered from src/lib/brokerRegistration.js, not state.broker_registered_name (null on national pages, and
              the state registry value also feeds the enrollment consent text, which is out of scope here). */}
          {PUCT_REGISTRATION_VERIFIED && (
            <p className="text-xs text-slate-400 mt-4" data-testid="registered-name">
              {REGISTERED_BROKER_FOOTER_IDENTITY}{" "}
              <span className="whitespace-nowrap" data-testid="registered-number">{REGISTERED_BROKER_FOOTER_NUMBER}</span>
            </p>
          )}
        </div>
        <nav aria-labelledby="footer-states-heading">
          <p id="footer-states-heading" className="text-overline text-slate-400 mb-4">{state ? state.name : "States"}</p>
          <ul className="space-y-2 text-sm">
            {state && isTrackActive(state, "residential") && (
              <li><Link to={statePath(state.slug, "plans")} className="hover:text-white text-slate-300">All plans</Link></li>
            )}
            {state && isTrackActive(state, "residential") && (
              <li><Link to={statePath(state.slug, "providers")} className="hover:text-white text-slate-300">Providers</Link></li>
            )}
            {!state && listStates()
              .filter((s) => SHOW_OTHER_STATES || s.code === PUBLIC_LAUNCH_STATE_CODE)
              .map((s) => (
                <li key={s.slug}><Link to={statePath(s.slug)} className="hover:text-white text-slate-300">{s.name}</Link></li>
              ))}
          </ul>
        </nav>
        <nav aria-labelledby="footer-company-heading">
          <p id="footer-company-heading" className="text-overline text-slate-400 mb-4">Company</p>
          <ul className="space-y-2 text-sm">
            <li><Link to="/how-it-works" className="hover:text-white text-slate-300">How it works</Link></li>
            <li><Link to="/bill-check" className="hover:text-white text-slate-300">Check your bill</Link></li>
            <li><Link to="/texas/esid-lookup" className="hover:text-white text-slate-300">Find your ESID</Link></li>
            {newsletterSignupEnabled() && (
              <li><Link to={NEWSLETTER_PATH} className="hover:text-white text-slate-300" data-testid="footer-newsletter-link">{NEWSLETTER_COPY.heading}</Link></li>
            )}
            <li><Link to="/about" className="hover:text-white text-slate-300" data-testid="footer-about-link">About</Link></li>
            <li><Link to="/help"className="hover:text-white text-slate-300" data-testid="footer-help-link">Help</Link></li>
          </ul>
        </nav>
      </div>
      <div className="border-t border-white/10 px-6 lg:px-10 py-6 max-w-[1400px] mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-xs text-slate-400">
        <span>© 2026 RateCurrent</span>
        {/* Trust-link row (Design Specification.md line 401). The "Broker Disclosures coming soon" label was removed per
            SEO_Copy_Rulings.md 2.5 (Section 17.4 defers that build; a coming-soon label implies broker status). */}
        <ul className="flex flex-wrap items-center gap-x-4 gap-y-1" data-testid="footer-trust-links">
          {/* Until the Owner fills in src/lib/legalConfig.js (LEGAL_FACTS_READY), these are plain "coming soon" labels. */}
          {LEGAL_FACTS_READY ? (
            <>
              <li><Link to="/privacy" className="hover:text-white text-slate-300">Privacy Policy</Link></li>
              <li><Link to="/terms" className="hover:text-white text-slate-300">Terms of Use</Link></li>
            </>
          ) : (
            <>
              <li className="text-slate-500" data-testid="footer-privacy-coming-soon">Privacy Policy (coming soon)</li>
              <li className="text-slate-500" data-testid="footer-terms-coming-soon">Terms of Use (coming soon)</li>
            </>
          )}
          <li><a href="mailto:support@ratecurrent.com" className="hover:text-white text-slate-300">Contact</a></li>
        </ul>
      </div>
    </footer>
  );
}
